import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { MapPin, Users, Calendar, Trophy, Settings, Plus, User, Shield, Image, Video, Eye } from 'lucide-react';
import { Team } from '@/types/database';
import { TeamPendingRequests } from './TeamPendingRequests';
import { TeamMembersSection } from './TeamMembersSection';
import { TeamPhotoGallery } from './TeamPhotoGallery';
import { TeamVideoGallery } from './TeamVideoGallery';
import { PublicTeamView } from './PublicTeamView';

import { SeparateTeamManagementModal } from './SeparateTeamManagementModal';
import { AddPlayerForm } from './AddPlayerForm';
import { AddCoachForm } from './AddCoachForm';
import { AddSupportStaffForm } from './AddSupportStaffForm';
import { TeamPhotoUploadForm } from './TeamPhotoUploadForm';
import { TeamVideoUploadForm } from './TeamVideoUploadForm';
import { TeamCodeManagement } from './TeamCodeManagement';
import { teamService } from '@/services/teamService';

interface TeamManagementViewProps {
  team: Team;
  currentUserId?: string;
}

export const TeamManagementView = ({ team, currentUserId }: TeamManagementViewProps) => {
  const [showManageModal, setShowManageModal] = useState(false);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [showCoachForm, setShowCoachForm] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [photoGalleryKey, setPhotoGalleryKey] = useState(0);
  const [viewAsPublic, setViewAsPublic] = useState(false);
  
  const handleLogoUpload = async (logoPath: string) => {
    try {
      await teamService.updateTeam(team.team_id, { team_logo: logoPath });
      // Refresh the page to show updated logo
      window.location.reload();
    } catch (error) {
      console.error('Error updating team logo:', error);
    }
  };

  const handleUpdate = () => {
    // Refresh the page to show updates
    window.location.reload();
  };

  // Handle photo gallery refresh
  const handlePhotoUpdate = () => {
    // Force refresh of photo gallery component by updating the key
    setPhotoGalleryKey(prev => prev + 1);
  };

  // If viewing as public, show the public view instead
  if (viewAsPublic) {
    return (
      <div className="space-y-4">
        {/* Preview Mode Notice */}
        <Card className="bg-yellow-900/30 border-yellow-600">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-yellow-300">
                <Eye className="h-5 w-5" />
                <span className="font-semibold">Public Preview Mode</span>
              </div>
              <div className="flex items-center gap-3">
                <Label htmlFor="preview-toggle" className="text-yellow-200 text-sm">
                  Exit Preview
                </Label>
                <Switch
                  id="preview-toggle"
                  checked={viewAsPublic}
                  onCheckedChange={setViewAsPublic}
                />
              </div>
            </div>
            <p className="text-yellow-200 text-sm mt-1">
              You are viewing your team page as the public sees it.
            </p>
          </CardContent>
        </Card>
        
        <PublicTeamView team={team} currentUserId={currentUserId} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Management Notice */}
      <Card className="bg-blue-900/30 border-blue-600">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-300">
              <Settings className="h-5 w-5" />
              <span className="font-semibold">Team Management View</span>
            </div>
            <div className="flex items-center gap-4">
              {/* View as Public Toggle */}
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-gray-400" />
                <Label htmlFor="public-toggle" className="text-gray-300 text-sm">
                  View as Public
                </Label>
                <Switch
                  id="public-toggle"
                  checked={viewAsPublic}
                  onCheckedChange={setViewAsPublic}
                />
              </div>
              
              <Button 
                onClick={() => setShowManageModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Team
              </Button>
            </div>
          </div>
          <p className="text-blue-200 text-sm mt-1">
            You are viewing this team as a manager. You can accept player requests, manage members, and update team information.
          </p>
        </CardContent>
      </Card>


      {/* Pending Requests Section - Only for team managers */}
      <TeamPendingRequests 
        teamId={team.team_id}
        currentUserId={currentUserId}
        canManage={true}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {team.team_description && (
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-300">{team.team_description}</p>
              </CardContent>
            </Card>
          )}

          {/* Team Stats/Info */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Team Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Sport: {team.sport_type || 'Not specified'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Age Group: {team.age_group || 'Open'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-sports-green" />
                  <span className="text-gray-300">Division: {team.division || 'General'}</span>
                </div>
                {team.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-sports-green" />
                    <span className="text-gray-300">{team.location}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Created</span>
                <span className="text-white">
                  {team.created_at ? new Date(team.created_at).toLocaleDateString() : 'Unknown'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Sport</span>
                <span className="text-white">{team.sport_type || 'Not specified'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Location</span>
                <span className="text-white">{team.city || team.location || 'Not specified'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Slug</span>
                <span className="text-white text-xs">{team.slug}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Code Management for Under 13 Teams */}
      <TeamCodeManagement 
        teamId={team.team_id}
        teamName={team.team_name}
        teamAgeGroup={team.age_group}
      />

      {/* Team Members Section - Shows current members with manage buttons */}
        <TeamMembersSection
          teamId={team.team_id}
          teamName={team.team_name}
          teamSport={team.sport_type}
          currentUserId={currentUserId}
          canManage={true}
          setShowPlayerForm={setShowPlayerForm}
          setShowCoachForm={setShowCoachForm}
          setShowSupportForm={setShowSupportForm}
        />

      {/* Team Photo Gallery - Shows photos without manage buttons */}
      <TeamPhotoGallery 
        teamId={team.team_id}
        canManage={true}
        key={`photo-gallery-${photoGalleryKey}`} // Force refresh when needed
      />

      {/* Team Video Gallery - Shows videos with manage buttons */}
      <TeamVideoGallery 
        teamId={team.team_id}
        canManage={true}
      />

      {/* Team Management Modal - Only for Logo */}
      <SeparateTeamManagementModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        teamId={team.team_id}
        teamName={team.team_name}
        teamSport={team.sport_type}
        onUpdate={handleUpdate}
      />

      {/* Individual Forms */}
      <AddPlayerForm
        isOpen={showPlayerForm}
        onClose={() => setShowPlayerForm(false)}
        teamId={team.team_id}
        teamName={team.team_name}
        teamSport={team.sport_type}
        onUpdate={handleUpdate}
      />

      <AddCoachForm
        isOpen={showCoachForm}
        onClose={() => setShowCoachForm(false)}
        teamId={team.team_id}
        teamName={team.team_name}
        onUpdate={handleUpdate}
      />

      <AddSupportStaffForm
        isOpen={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        teamId={team.team_id}
        teamName={team.team_name}
        onUpdate={handleUpdate}
      />

        <TeamPhotoUploadForm
          isOpen={showPhotoForm}
          onClose={() => setShowPhotoForm(false)}
          teamId={team.team_id}
          teamName={team.team_name}
          onUpdate={handlePhotoUpdate}
        />

      <TeamVideoUploadForm
        isOpen={showVideoForm}
        onClose={() => setShowVideoForm(false)}
        teamId={team.team_id}
        teamName={team.team_name}
        onUpdate={handleUpdate}
      />
    </div>
  );
};